// Helpers for supplement-plan duration / status UI, shared by SupplementPlanSheet (coach)
// and SupplementsPage (client). The plan stores `duration_days` (an integer); the UI lets
// the user pick a value in days / weeks / months (weeks = 7d, months = 30d — approximate;
// the "ends on <date>" preview shown alongside makes the real end date concrete).

// Returns a translated status label for a supplement plan, or null if no label should be shown.
// plan: the plan doc; t: the i18next t() function.
export function planStatusLabel(plan, t) {
  if (plan.active && plan.duration_days && plan.activated_at) {
    const end = new Date(new Date(plan.activated_at).getTime() + plan.duration_days * 86400000);
    const days = Math.ceil((end - new Date()) / 86400000);
    if (days <= 0) return null; // past due but backend hasn't deactivated yet — no label until confirmed
    if (days === 1) return t('supplementPlan.endsToday');
    return t('supplementPlan.endsInDays', { count: days });
  }
  if (!plan.active && plan.deactivated_reason === 'duration_elapsed') return t('supplementPlan.ended');
  // Active plans (incl. ones with a duration but no activation anchor yet) show "No expiry";
  // inactive plans that weren't auto-deactivated show no label.
  return plan.active ? t('supplementPlan.noExpiry') : null;
}

export const DURATION_UNITS = [
  { key: 'days', factor: 1 },
  { key: 'weeks', factor: 7 },
  { key: 'months', factor: 30 },
];

// Largest value allowed for a given unit so the day-count stays within the backend's 1..365 range.
export function maxForUnit(unit) {
  const u = DURATION_UNITS.find((x) => x.key === unit) || DURATION_UNITS[0];
  return Math.floor(365 / u.factor);
}

// Given a day-count, pick the largest unit it divides cleanly into (months > weeks > days).
// Falsy/invalid input → { value: '', unit: 'days' } (i.e. "no expiry").
export function splitDurationDays(days) {
  const d = parseInt(days, 10);
  if (!d || d < 1) return { value: '', unit: 'days' };
  if (d % 30 === 0) return { value: String(d / 30), unit: 'months' };
  if (d % 7 === 0) return { value: String(d / 7), unit: 'weeks' };
  return { value: String(d), unit: 'days' };
}

// Convert a (value, unit) pair from the picker back to an integer day-count.
// Blank/zero value → 0 (the backend's "clear duration" sentinel on updates).
export function durationDaysFrom(value, unit) {
  const v = parseInt(value, 10);
  if (!v || v < 1) return 0;
  const u = DURATION_UNITS.find((x) => x.key === unit) || DURATION_UNITS[0];
  return v * u.factor;
}

// The date this plan would end, as a Date — or null if no duration.
// If the plan is already running (has activated_at) AND the duration is unchanged from what's
// stored, count from the original activation. Otherwise (new plan, or the duration is being
// changed — which restarts the clock) count from now.
export function previewEndDate(plan, newDurationDays) {
  const d = parseInt(newDurationDays, 10);
  if (!d || d < 1) return null;
  const anchor = plan && plan.activated_at && d === plan.duration_days
    ? new Date(plan.activated_at)
    : new Date();
  return new Date(anchor.getTime() + d * 86400000);
}

// Locale-aware short date string for the preview line.
export function formatEndDate(date, locale) {
  if (!date) return '';
  try {
    return date.toLocaleDateString(locale || 'en', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return date.toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}
